import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Mail, Phone, MapPin, Share2 } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { LoadingScreen } from "@/components/common";

export default function AdminContactEdit() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/content/contact").then((r) => setD(r.data)).catch(() => toast.error("Failed to load")); }, []);
  if (!d) return <DashboardShell menu={adminMenu} title="Edit Contact Details"><LoadingScreen /></DashboardShell>;

  const set = (k, v) => setD({ ...d, [k]: v });
  const setSocial = (k, v) => setD({ ...d, social: { ...(d.social || {}), [k]: v } });
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/content/contact", d); setD(r.data); toast.success("Contact details updated — live on the website"); }
    catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const F = ({ label, k, area, ph }) => (
    <div>
      <Label className="text-xs font-semibold text-navy-700">{label}</Label>
      {area ? <Textarea className="mt-1" rows={2} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`contact-f-${k}`} />
        : <Input className="mt-1" placeholder={ph} value={d[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`contact-f-${k}`} />}
    </div>
  );

  return (
    <DashboardShell menu={adminMenu} title="Edit Contact Details">
      <PageHeader title="Contact Information" subtitle="Update contact details shown on the public Contact page. Changes go live immediately."
        action={<Button className="bg-brand hover:bg-brand-600" disabled={saving} onClick={save} data-testid="contact-save"><Save size={16} className="mr-1.5" /> Save Changes</Button>} />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4">
          <h3 className="font-heading font-semibold text-navy-900 flex items-center gap-2"><Mail size={18} className="text-brand" /> Company & Contact</h3>
          <F label="Company Name" k="company_name" />
          <F label="Operated By" k="operated_by" />
          <F label="Intro Text" k="intro" area />
          <F label="Email" k="email" ph="name@example.com" />
          <F label="Phone" k="phone" ph="+91 ..." />
          <F label="WhatsApp" k="whatsapp" ph="+91 ..." />
          <F label="Address" k="address" area />
          <div className="flex items-center justify-between border-t border-navy-200 pt-4">
            <div><Label className="text-sm font-semibold text-navy-800">Enquiry Form</Label><p className="text-xs text-navy-500">Show the contact form on the public page</p></div>
            <Switch checked={d.form_enabled !== false} onCheckedChange={(v) => set("form_enabled", v)} data-testid="contact-form-toggle" />
          </div>
        </div>
        <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4 self-start">
          <h3 className="font-heading font-semibold text-navy-900 flex items-center gap-2"><Share2 size={18} className="text-brand" /> Social Media Links</h3>
          {["facebook", "instagram", "linkedin", "twitter"].map((s) => (
            <div key={s}>
              <Label className="text-xs font-semibold text-navy-700 capitalize">{s}</Label>
              <Input className="mt-1" placeholder="https://..." value={(d.social || {})[s] || ""} onChange={(e) => setSocial(s, e.target.value)} data-testid={`contact-social-${s}`} />
            </div>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
